import type { FailReasonValues, Workflow } from '@repo/common';

import { ActionType, FailReason, Step } from '@repo/common';
import { HTTPError } from 'ky';

export type ExtractedBffError = {
  code?: string;
  details?: Record<string, unknown>;
  message?: string;
  retryable?: boolean;
  status?: number;
};

export async function extractBffErrorFromKyHttpError(err: unknown): Promise<ExtractedBffError | null> {
  if (!(err instanceof HTTPError)) {
    return null;
  }

  const status = err.response.status;
  const responseData = await err.response.clone().json().catch(() => null);

  const maybeError =
    responseData && typeof responseData === 'object' ? (responseData as { error?: unknown }).error : undefined;

  if (!maybeError || typeof maybeError !== 'object') {
    return null;
  }

  const { code } = maybeError as { code?: unknown };
  const { message } = maybeError as { message?: unknown };
  const { retryable } = maybeError as { retryable?: unknown };
  const { details } = maybeError as { details?: unknown };

  return {
    code: typeof code === 'string' ? code : undefined,
    message: typeof message === 'string' ? message : undefined,
    retryable: typeof retryable === 'boolean' ? retryable : undefined,
    details: details && typeof details === 'object' ? (details as Record<string, unknown>) : undefined,
    status,
  };
}

export function mapBffErrorCodeToFailReason(code?: string): FailReasonValues {
  switch (code) {
    case 'DOCUMENT_NOT_ALLOWED':
      return FailReason.DOCUMENT_NOT_ALLOWED;
    case 'DOCUMENT_NOT_RECOGNIZED':
      return FailReason.DOCUMENT_NOT_RECOGNIZED;
    case 'DOCUMENT_CAPTURED_INCORRECTLY':
      return FailReason.DOCUMENT_CAPTURED_INCORRECTLY;
    case 'BAD_LIGHTNING_OR_LOW_QUALITY':
      return FailReason.BAD_LIGHTNING_OR_LOW_QUALITY;

    case 'SESSION_EXPIRED':
      return FailReason.SESSION_EXPIRED;
    case 'SESSION_NOT_FOUND':
    case 'NOT_FOUND':
      return FailReason.NOT_FOUND;
    case 'SUSPENDED':
      return FailReason.SUSPENDED;
    case 'UNAUTHORIZED':
      return FailReason.UNAUTHORIZED;

    default:
      return FailReason.UNABLE_TO_VERIFY;
  }
}

export function buildRedirectFailWorkflow(failReason: FailReasonValues, redirectUrl?: string): Workflow {
  return {
    context: {},
    nextStep: {
      value: Step.FAIL,
      failReason,
      action: {
        type: ActionType.REDIRECT,
        data: {
          url: redirectUrl ?? 'http://localhost:3000/result',
          payload: {
            result: 'error',
          },
        },
      },
    },
  };
}
